import {
  CampaignApiDto,
  CampaignApiResponseDto,
  CampaignApiType,
  CampaignAudienceType,
} from "./CampaignApiDto";
import { formatUtcToLocalDateTimeInGermanFormat } from "../../../components/atoms/Utils";
import {
  CampaignExperimentalDto,
  CampaignExperimentalListResponseDto,
  CampaignExperimentalStatus,
} from "./CampaignExperimentalDto";
import { TypeMappers, StatusUtils } from "./CampaignMapperUtils";
import {
  CampaignFormDto,
  CouponType,
  CouponValidityType,
  CreateCampaignRequest,
  ExecutionMethod,
  EventType,
  TargetAudienceType,
} from "./CampaignFormDto";

export class CampaignMapper {
  static mapApiResponseToFrontend(
    apiResponse: CampaignApiResponseDto,
    requestedPageNumber = 1,
    requestedPageSize = 10
  ): CampaignExperimentalListResponseDto {
    const campaigns = apiResponse.data.map((apiCampaign) => this.mapApiCampaignToFrontend(apiCampaign));

    const totalPages = Math.ceil(apiResponse.total / requestedPageSize);

    return {
      campaigns,
      totalCount: apiResponse.total,
      pageSize: requestedPageSize,
      pageNumber: requestedPageNumber,
      totalPages,
    };
  }

  static mapApiCampaignToFrontend(apiCampaign: CampaignApiDto): CampaignExperimentalDto {
    return {
      id: apiCampaign.campaignNumber,
      name: apiCampaign.name,
      description: apiCampaign.description,
      campaignType: TypeMappers.apiTypeToFrontend(apiCampaign.type),
      status: (apiCampaign.status as CampaignExperimentalStatus) || StatusUtils.inferStatus(apiCampaign),
      assignment: {
        type: TypeMappers.audienceTypeToAssignmentType(apiCampaign.audienceType),
        segmentIds: apiCampaign.audienceType === CampaignAudienceType.SEGMENT ? apiCampaign.targets : undefined,
        circleIds: apiCampaign.audienceType === CampaignAudienceType.CIRCLE ? apiCampaign.targets : undefined,
        estimatedReach: 0,
      },
      couponTemplateId: apiCampaign.couponTemplateNumber,
      automationSettings: apiCampaign.eventType
        ? {
            triggerEvent: TypeMappers.eventTypeToTriggerEvent(apiCampaign.eventType),
            timingOffset: Math.abs(apiCampaign.offsetDays || 0),
            timingOffsetUnit: "DAYS" as any,
            triggerTime: apiCampaign.executionHour,
          }
        : undefined,
      scheduleSettings: {
        startDate: apiCampaign.startDate,
        endDate: apiCampaign.endDate,
        timezone: "Europe/Berlin",
        executionTime: apiCampaign.executionHour,
      },
      notificationPlan: TypeMappers.notificationPlanToChannels(apiCampaign.notificationPlan),
      statistics: apiCampaign.statistics,
      createdAt: apiCampaign.createdAt || new Date().toISOString(),
      updatedAt: apiCampaign.updatedAt || new Date().toISOString(),
      createdBy: apiCampaign.createdBy || "System",
      triggerType: apiCampaign.eventType,
      audienceType: apiCampaign.audienceType,
      notificationChannel: apiCampaign.notificationPlan,
      validity: this.formatValidity(
        apiCampaign.startDate,
        apiCampaign.endDate,
        apiCampaign.type,
        apiCampaign.executionTime
      ),
    };
  }

  static mapApiResponseToFormData(apiCampaign: CampaignApiDto): CampaignFormDto {
    const executionMethod =
      apiCampaign.type === CampaignApiType.ONE_TIME ? ExecutionMethod.SCHEDULED : ExecutionMethod.EVENT_DRIVEN;

    const { targetAudienceType, segmentNumbers, circleNumbers } = this.mapAudienceFromApi(apiCampaign);
    const eventType = this.mapBackendEventType(apiCampaign.eventType);
    const couponValidityType = this.mapCouponValidityType(apiCampaign);
    const scheduledDate = this.parseExecutionTime(apiCampaign.executionTime);
    const birthdayTime = this.parseExecutionHour(apiCampaign.executionHour);
    const couponType = (apiCampaign.couponType as CouponType) || CouponType.FREE;

    return {
      name: apiCampaign.name,
      description: apiCampaign.description,
      executionMethod,
      scheduledDate,
      eventType,
      birthdayDaysBefore: apiCampaign.offsetDays,
      birthdayTime,
      tierUpdateTransitions: apiCampaign.tierUpdateTransitions || [],
      couponType,
      couponNumber: apiCampaign.couponTemplateNumber || "",
      startDate: apiCampaign.startDate,
      endDate: apiCampaign.endDate,
      couponValidityType,
      validityDays: apiCampaign.validityDays,
      fixedEndDate: apiCampaign.fixedExpiryDate,
      targetAudienceType,
      segmentNumbers,
      circleNumbers,
      notificationPlan: apiCampaign.notificationPlan,
    };
  }

  static mapFormDataToApiRequest(formData: CampaignFormDto): CreateCampaignRequest {
    const type = formData.executionMethod === ExecutionMethod.SCHEDULED ? "ONE_TIME" : "RECURRING";
    const { audienceType, targets } = this.mapTargetAudience(formData);
    const validityConfig = this.mapCouponValidity(formData);

    const request: CreateCampaignRequest = {
      name: formData.name,
      description: formData.description,
      type,
      couponTemplateNumber: formData.couponNumber,
      audienceType,
      startDate: formData.startDate,
      endDate: formData.endDate,
    };

    this.addExecutionMethodFields(request, formData);
    this.addOptionalFields(request, formData, targets, validityConfig);

    return request;
  }

  static mapFrontendCreateRequestToApi(formData: CampaignFormDto): CreateCampaignRequest {
    return this.mapFormDataToApiRequest(formData);
  }

  private static formatValidity(
    startDate: string,
    endDate?: string,
    type?: CampaignApiType,
    executionTime?: string
  ): string {
    if (type === CampaignApiType.ONE_TIME && executionTime) {
      const formattedDateTime = formatUtcToLocalDateTimeInGermanFormat(executionTime);
      return formattedDateTime || executionTime;
    }

    const start = new Date(startDate).toLocaleDateString("de-DE");
    if (endDate) {
      const end = new Date(endDate).toLocaleDateString("de-DE");
      return `${start} - ${end}`;
    }
    return `Ab ${start}`;
  }

  private static mapAudienceFromApi(apiResponse: CampaignApiDto): {
    targetAudienceType: TargetAudienceType;
    segmentNumbers: string[];
    circleNumbers: string[];
  } {
    if (apiResponse.audienceType === CampaignAudienceType.SEGMENT) {
      return {
        targetAudienceType: TargetAudienceType.SEGMENT,
        segmentNumbers: apiResponse.targets || [],
        circleNumbers: [],
      };
    }

    if (apiResponse.audienceType === CampaignAudienceType.CIRCLE) {
      return {
        targetAudienceType: TargetAudienceType.CIRCLES,
        segmentNumbers: [],
        circleNumbers: apiResponse.targets || [],
      };
    }

    return {
      targetAudienceType: TargetAudienceType.ALL_CUSTOMERS,
      segmentNumbers: [],
      circleNumbers: [],
    };
  }

  private static mapBackendEventType(backendEventType?: string): EventType | undefined {
    const eventTypeMap: Record<string, EventType> = {
      BIRTHDAY: EventType.BIRTHDAY,
      CIRCLE_UPGRADE: EventType.TIER_UPDATE,
      EMAIL_OPT_IN: EventType.EMAIL_OPTIN,
      REGISTRATION: EventType.REGISTRATION,
    };

    return backendEventType ? eventTypeMap[backendEventType] : undefined;
  }

  private static mapCouponValidityType(apiResponse: CampaignApiDto): CouponValidityType {
    if (apiResponse.oneTimeValidityType === "DAYS_AFTER_PURCHASE") {
      return CouponValidityType.DAYS_AFTER_PURCHASE;
    }

    if (apiResponse.oneTimeValidityType === "FIXED_EXPIRY_DATE") {
      return CouponValidityType.FIXED_END_DATE;
    }

    if (apiResponse.validityDays !== undefined && apiResponse.validityDays > 0) {
      return CouponValidityType.DAYS_AFTER_PURCHASE;
    }

    if (apiResponse.fixedExpiryDate) {
      return CouponValidityType.FIXED_END_DATE;
    }

    return CouponValidityType.DEFAULT_FROM_CAMPAIGN;
  }

  private static parseExecutionTime(executionTime?: string): string | undefined {
    if (!executionTime) return undefined;

    const utcDate = new Date(executionTime);
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, "0");
    const day = String(utcDate.getDate()).padStart(2, "0");
    const hours = String(utcDate.getHours()).padStart(2, "0");
    const minutes = String(utcDate.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private static parseExecutionHour(executionHour?: string): string | undefined {
    if (!executionHour) return undefined;

    const hasTimezone = executionHour.includes("+") || executionHour.includes("-", 3);

    if (hasTimezone) {
      const timeMatch = /^(\d{2}:\d{2})/.exec(executionHour);
      return timeMatch ? timeMatch[1] : undefined;
    }

    const today = new Date().toISOString().split("T")[0];
    const utcDateTime = `${today}T${executionHour}Z`;
    const utcDate = new Date(utcDateTime);

    const hours = String(utcDate.getHours()).padStart(2, "0");
    const minutes = String(utcDate.getMinutes()).padStart(2, "0");

    return `${hours}:${minutes}`;
  }

  private static mapTargetAudience(formData: CampaignFormDto): {
    audienceType: "ALL" | "SEGMENT" | "CIRCLE";
    targets?: string[];
  } {
    if (formData.targetAudienceType === TargetAudienceType.ALL_CUSTOMERS) {
      return { audienceType: "ALL" };
    }

    if (formData.targetAudienceType === TargetAudienceType.SEGMENT) {
      return { audienceType: "SEGMENT", targets: formData.segmentNumbers };
    }

    return { audienceType: "CIRCLE", targets: formData.circleNumbers };
  }

  private static mapEventType(eventType?: EventType): "BIRTHDAY" | "CIRCLE_UPGRADE" | "EMAIL_OPT_IN" | "REGISTRATION" | undefined {
    if (eventType === EventType.BIRTHDAY) return "BIRTHDAY";
    if (eventType === EventType.TIER_UPDATE) return "CIRCLE_UPGRADE";
    if (eventType === EventType.EMAIL_OPTIN) return "EMAIL_OPT_IN";
    if (eventType === EventType.REGISTRATION) return "REGISTRATION";
    return undefined;
  }

  private static formatExecutionHour(birthdayTime?: string): string | undefined {
    if (!birthdayTime) return undefined;

    const timezoneOffset = new Date().getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60)
      .toString()
      .padStart(2, "0");
    const offsetMinutes = (Math.abs(timezoneOffset) % 60).toString().padStart(2, "0");
    const sign = timezoneOffset <= 0 ? "+" : "-";

    return `${birthdayTime}:00${sign}${offsetHours}:${offsetMinutes}`;
  }

  private static formatExecutionTime(scheduledDate?: string): string | undefined {
    if (!scheduledDate) return undefined;

    const localDate = new Date(scheduledDate);
    if (Number.isNaN(localDate.getTime())) {
      return undefined;
    }

    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");

    const offsetMinutes = -localDate.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absMinutes = Math.abs(offsetMinutes);
    const offsetHours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
    const offsetRemainMinutes = String(absMinutes % 60).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:00${sign}${offsetHours}:${offsetRemainMinutes}`;
  }

  private static addExecutionMethodFields(request: CreateCampaignRequest, formData: CampaignFormDto): void {
    if (formData.executionMethod === ExecutionMethod.SCHEDULED) {
      const executionTime = this.formatExecutionTime(formData.scheduledDate);
      if (executionTime) request.executionTime = executionTime;
    } else {
      const eventType = this.mapEventType(formData.eventType);
      const executionHour = this.formatExecutionHour(formData.birthdayTime);

      if (eventType) request.eventType = eventType;
      if (formData.birthdayDaysBefore !== undefined) request.offsetDays = formData.birthdayDaysBefore;
      if (executionHour) request.executionHour = executionHour;
    }
  }

  private static mapCouponValidity(formData: CampaignFormDto): {
    validityDays?: number;
    oneTimeValidityType?: "DAYS_AFTER_PURCHASE" | "FIXED_EXPIRY_DATE";
    fixedExpiryDate?: string;
  } {
    if (formData.couponValidityType === CouponValidityType.DAYS_AFTER_PURCHASE) {
      return {
        validityDays: formData.validityDays,
        oneTimeValidityType: "DAYS_AFTER_PURCHASE",
      };
    }

    if (formData.couponValidityType === CouponValidityType.FIXED_END_DATE) {
      return {
        oneTimeValidityType: "FIXED_EXPIRY_DATE",
        fixedExpiryDate: formData.fixedEndDate,
      };
    }

    return {};
  }

  private static addOptionalFields(
    request: CreateCampaignRequest,
    formData: CampaignFormDto,
    targets?: string[],
    validityConfig?: {
      validityDays?: number;
      oneTimeValidityType?: "DAYS_AFTER_PURCHASE" | "FIXED_EXPIRY_DATE";
      fixedExpiryDate?: string;
    }
  ): void {
    if (targets && targets.length > 0) {
      request.targets = targets;
    }

    if (validityConfig?.validityDays !== undefined) {
      request.validityDays = validityConfig.validityDays;
    }

    if (validityConfig?.oneTimeValidityType) {
      request.oneTimeValidityType = validityConfig.oneTimeValidityType;
    }

    if (validityConfig?.fixedExpiryDate) {
      request.fixedExpiryDate = validityConfig.fixedExpiryDate;
    }

    if (formData.notificationPlan?.trim()) {
      request.notificationPlan = formData.notificationPlan;
    }
  }
}
